/* eslint-disable @typescript-eslint/no-explicit-any */
import { BehaviorSubject, combineLatest, filter, first, map, ReplaySubject } from "rxjs";
import { getUserService } from "./user.service";
import { env } from "env";
import { getClientsService } from "./clients.service";
import { getClientService } from "./client.service";
import { initService } from "helpers/initService";
import { FeatureFlag, getFFService } from "./ff.service";
import { ApiSchema } from "@api-types";
import { uuidv7 } from "helpers/uuidv7";
import { getSessionService } from "./session.service";
import { getOdataActionTrackingService } from "./odataActionTracking.service";

type SegmentEvents =
  | "Login"
  | "Logout"
  | "ProjectExported"
  | "ProjectCreated"
  | "AssistantThreadCreated"
  | "ProjectSubmitted"
  | "ProjectApproved"
  | "ProjectDeclined"
  | "ProjectResetToDraft"
  | "ProjectEditViewed"
  | "ProjectEditSectionScrolledTo"
  | "ExportPlanPanelOpened"
  | "FollowersPanelOpened"
  | "AudienceCustomerPanelTargeted"
  | "AudienceCustomerPanelCleared"
  | "AudienceIdeallyPanelTargeted"
  | "AudienceIdeallyPanelCleared"
  | "FrameworkSelected"
  | "FrameworkSwitchOpened"
  | "FrameworkSwitched"
  | "ProjectAddOnToggled"
  | "MarketReachConfigured"
  | "PriceSensitivityConfigured"
  | "ProjectBrandFitSelected"
  | "ProjectCategorySelected"
  | "ProjectSampleSizeChanged"
  | "ProjectScheduleChanged"
  | "UserInvited"
  | "TeamCreated"
  | "TransactionListExported"
  | "SaveAsTemplateTurnedOn"
  | "QuestionSaved"
  | "CreatePowerPointClicked"
  | "PowerPointExported"
  | "BenchmarkDropdownClick"
  | "BenchmarkSelection"
  | "ShareIconClick"
  | "CopyShareLinkClick"
  | "SummaryHover"
  | "SummaryCopyIconClick"
  | "FilterClick"
  | "ExpandedFiltersClick"
  | "PostRequest"
  | "PatchRequest"
  | "DeleteRequest"
  | "TabFocused"
  | "TabLostFocus"
  | "TabClosed"
  | "InternalLinkClicked"
  | "ButtonClicked"
  | "OnboardingStarted"
  | "OnboardingFinished"
  | "OnboardingAbandoned";

// Matches MainModule/ResultModule routes: /:clientId/(project|projects|project-surveys|result|results)/:projectId/...
const PROJECT_ID_PATH_REGEX = /\/(?:project|projects|project-surveys|result|results)\/([^/]+)/;
// Matches GroupsModule/AdminModule routes: /:clientId/(groups|project-groups)/:projectGroupId/...
const PROJECT_GROUP_ID_PATH_REGEX = /\/(?:groups|project-groups)\/([^/]+)/;

const parseProjectId = (): string | null => {
  return window.location.pathname.match(PROJECT_ID_PATH_REGEX)?.[1] ?? null;
};

const parseProjectGroupId = (): string | null => {
  return window.location.pathname.match(PROJECT_GROUP_ID_PATH_REGEX)?.[1] ?? null;
};

class SegmentService {
  private readonly initialized$ = new BehaviorSubject<boolean>(false);
  private readonly identifyComplete$ = new ReplaySubject<boolean>(1);
  private readonly envlope$ = new BehaviorSubject<Map<unknown, object>>(new Map());

  constructor() {
    getOdataActionTrackingService().odataActionTracked$.subscribe(({ type, params }) => {
      if (!this.initialized$.value) {
        return;
      }
      this.trackEvent(type as SegmentEvents, params);
    });
  }

  /**
   * Keyed by the caller (bloc instance or hook mount) so multiple concurrently mounted
   * contributors (e.g. a page bloc and a nested component) compose instead of overwriting
   * each other. Pass `null` as the envlope to remove that caller's slice (e.g. on unmount).
   */
  public setEnvlope = (key: unknown, envlope: object | null) => {
    const next = new Map(this.envlope$.value);
    envlope === null ? next.delete(key) : next.set(key, envlope);
    this.envlope$.next(next);
  };

  public init = (): void => {
    if (this.initialized$.value || env.VITE_LINKED_IN_DISABLED || window.isUnderTest) {
      return;
    }
    this.initialized$.next(true);
    this.loadScript();
    /**
     * Handle login
     * only after a login (aka we are redirected from aws cognito) and for non superAdmins
     * we call
     * - identify
     * - track the login
     * - call group to update the clients assotiation (should this be done on the be and hubspot sync?)
     */
    combineLatest([
      getUserService().user$.pipe(filter((user) => !!user)),
      getUserService().policies$.pipe(filter((h) => !!h)),
      getUserService().hmac$.pipe(filter((h) => h !== undefined)),
      combineLatest([getClientsService().clients$, getClientService().client$]).pipe(
        map(([clients, client]) => (clients?.length ? clients : client ? [client] : [])),
        filter((c) => c?.length > 0),
      ),
      getClientService().extendedTeams$.pipe(filter((c) => c?.length > 0)),
    ]).subscribe(([user, policy, hmac, clients, extendedTeams]) => {
      console.info("%c  identify  ", "background-color: green");
      analytics.identify(
        `${user.id}`,
        {
          actorKind: "user",
          email: user.email,
          firstName: user.firstName!,
          lastName: user.lastName!,
          role: policy.includes("TeamViewer") ? "Viewer" : policy.includes("TeamUser") ? "User" : "Admin",
          isIdeallyAdmin: user.isIdeallyAdmin,
          emailDomain: user.email.split("@")[1],
        },
        {
          integrations: {
            Intercom: {
              user_hash: hmac,
            },
          },
        },
      );
      setTimeout(() => {
        if ((window as any).intercomSettings) {
          (window as any).intercomSettings.z_index = 9;
        } else {
          (window as any).intercomSettings = {
            z_index: 9,
          };
        }
      });
      /**
       * note that there is an issue here as this is only additive
       * it won't remove companies you have been already associated with
       */
      if (!user.isIdeallyAdmin) {
        const clientMap = new Map(
          clients.map((client) => {
            const team = client.tier === "Team" ? extendedTeams.find((t) => t.teamId === client.id) : null;
            return [team?.orgId || client.id, team?.orgName || client.name];
          }),
        );
        clientMap.forEach((name, id) => {
          analytics.group(`${id}`, { name: name, clientName: name, clientId: id });
        });
      }
      this.identifyComplete$.next(true);
      this.trackEvent("Login");
    });
    window.addEventListener("beforeunload", () => {
      this.initialized$.value &&
        this.trackEvent("TabClosed", {
          tabId: getSessionService().getOrCreateTabId(),
        });
    });

    document.addEventListener("visibilitychange", () => {
      this.trackEvent(document.visibilityState === "visible" ? "TabFocused" : "TabLostFocus", {
        tabId: getSessionService().getOrCreateTabId(),
      });
    });
  };

  public pageview = (name: string) => {
    this.runIfTracked(() => analytics.page(name));
  };

  public trackEvent = (type: SegmentEvents, params?: object) => {
    if (!this.initialized$.value) return;
    this.runIfTracked((client, ffs) =>
      analytics.track(
        type,
        Object.fromEntries(
          Object.entries({
            clientId: client?.id,
            clientTier: client?.tier,
            orgId: client?.orgId,
            enterpriseId: client?.enterpriseId,
            featureFlags: ffs?.map((f) => f.ff),
            tabId: getSessionService().getOrCreateTabId(),
            page: window.location.pathname,
            pageQuery: window.location.search || undefined,
            projectId: parseProjectId(),
            projectGroupId: parseProjectGroupId(),
            ...Array.from(this.envlope$.value.values()).reduce((acc, slice) => Object.assign(acc, slice), {}),
            ...(params ?? {}),
            eventId: uuidv7(),
          }).filter(([_, v]) => v !== undefined && v !== null),
        ),
      ),
    );
  };

  private readonly runIfTracked = (
    fn: (client: ApiSchema.ClientsView | null, ffs: FeatureFlag[] | null) => void,
  ) => {
    combineLatest([this.identifyComplete$, getClientService().client$, getFFService().ffs$])
      .pipe(first())
      .subscribe(([_, client, ffs]) => {
        getUserService()
          .user$.pipe(first())
          .subscribe((user) => {
            !user?.isIdeallyAdmin && fn(client, ffs);
          });
      });
  };

  private readonly loadScript = (): void => {
    const analytics = (window.analytics = window.analytics || []);
    if (!analytics.initialize)
      if (analytics.invoked)
        window.console && console.error && console.error("Segment snippet included twice.");
      else {
        analytics.invoked = !0;
        analytics.methods = [
          "trackSubmit",
          "trackClick",
          "trackLink",
          "trackForm",
          "pageview",
          "identify",
          "reset",
          "group",
          "track",
          "ready",
          "alias",
          "debug",
          "page",
          "screen",
          "once",
          "off",
          "on",
          "addSourceMiddleware",
          "addIntegrationMiddleware",
          "setAnonymousId",
          "addDestinationMiddleware",
          "register",
        ];

        analytics.factory = function (e: any) {
          return function () {
            // eslint-disable-next-line prefer-spread, prefer-rest-params
            if (window.analytics.initialized) return window.analytics[e].apply(window.analytics, arguments);
            // eslint-disable-next-line prefer-rest-params
            const i = Array.prototype.slice.call(arguments);
            if (
              typeof e === "string" &&
              ["track", "screen", "alias", "group", "page", "identify"].indexOf(e) > -1
            ) {
              const c = document.querySelector("link[rel='canonical']");
              i.push({
                __t: "bpc",
                c: (c && c.getAttribute("href")) || void 0,
                p: location.pathname,
                u: location.href,
                s: location.search,
                t: document.title,
                r: document.referrer,
              });
            }
            i.unshift(e);
            analytics.push(i);
            return analytics;
          };
        };
        for (let i = 0; i < analytics.methods.length; i++) {
          const key = analytics.methods[i];
          analytics[key] = analytics.factory(key);
        }
        analytics.load = function (key: string, i: unknown) {
          const t = document.createElement("script");
          t.type = "text/javascript";
          t.async = !0;
          t.src = "https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";
          const n = document.getElementsByTagName("script")[0];
          n?.parentNode?.insertBefore(t, n);
          analytics._loadOptions = i;
        };
        analytics._writeKey = env.VITE_SEGMENT_ANALYTICS;
        analytics.SNIPPET_VERSION = "5.2.0";
        analytics.load(env.VITE_SEGMENT_ANALYTICS);
      }
  };
}

let segmentService: Readonly<SegmentService> | null = null;

export const getSegmentService = () => {
  if (!segmentService) {
    segmentService = initService(() => new SegmentService());
  }
  return segmentService;
};
